import { useMemo, useState } from "react";
import type { User, UserStatusFilter } from "../Types/UserTypes";
import { USERS_PAGE_SIZE } from "../Utils/UserConstants";

export const useUserFilters = (users: User[]) => {
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState<UserStatusFilter>("all");
  const [roleFilter, setRoleFilter] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);

  const filteredUsers = useMemo(() => {
    const query = searchText.toLowerCase();
    return users.filter(user => {
      const matchSearch =
        user.name?.toLowerCase().includes(query) || user.email?.toLowerCase().includes(query);
      const matchStatus = statusFilter === "all" || (statusFilter === "active") === user.isActive;
      const matchRole = roleFilter.length === 0 || roleFilter.includes(user.accessLevel);
      return matchSearch && matchStatus && matchRole;
    });
  }, [users, searchText, statusFilter, roleFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / USERS_PAGE_SIZE));
  const displayedPage = Math.min(currentPage, totalPages);
  const paginatedUsers = filteredUsers.slice(
    (displayedPage - 1) * USERS_PAGE_SIZE,
    displayedPage * USERS_PAGE_SIZE
  );

  const changeSearchText = (value: string) => {
    setSearchText(value);
    setCurrentPage(1);
  };

  const changeStatusFilter = (value: UserStatusFilter) => {
    setStatusFilter(value);
    setCurrentPage(1);
  };

  const changeRoleFilter = (value: string[]) => {
    setRoleFilter(value);
    setCurrentPage(1);
  };

  return {
    searchText,
    statusFilter,
    roleFilter,
    filteredUsers,
    paginatedUsers,
    displayedPage,
    totalPages,
    changeSearchText,
    changeStatusFilter,
    changeRoleFilter,
    goToPreviousPage: () => setCurrentPage(Math.max(1, displayedPage - 1)),
    goToNextPage: () => setCurrentPage(Math.min(totalPages, displayedPage + 1)),
  };
};
